import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    Button,
    Chip,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { useLang } from "../../context/LanguageContext";
import { formatDate } from "../../Utils/dateFormatter";

const BuyInvoice = () => {
    const { t, lang } = useLang();
    const params = useParams();
    const authHeader = useAuthHeader();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);

    useEffect(() => {
        if (params?.id) {
            const fetchInvoice = async () => {
                const resp = await fetch(
                    `${REACT_APP_BACKEND_URL}/invoices/buyInvoice/${params.id}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: authHeader,
                            "Content-Type": "application/json",
                        },
                    },
                );
                const result = await resp.json();
                console.log(result);
                setInvoice(result);
            };
            fetchInvoice();
        }
    }, [authHeader, params.id]);

    // Parse items from JSON string
    const items = invoice ? JSON.parse(invoice.items || "{}") : [];

    if (!invoice && params?.id) {
        // Loading state or simple null check return
        return <Typography sx={{ p: 4 }}>Loading...</Typography>;
    }

    if (!invoice) {
        return <Typography sx={{ p: 4 }}>{t("NO_INVOICE_FOUND")}</Typography>;
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ReceiptLongIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t("INVOICE_DETAILS")} #{invoice.id}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {new Date(invoice.createdAt).toLocaleString()}
                        </Typography>
                    </Box>
                </Box>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        color: "white",
                        bgcolor: "rgba(255,255,255,0.15)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                    }}
                >
                    {t("BACK")}
                </Button>
            </Paper>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Employee Info */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <PersonIcon color="primary" />
                            <Typography variant="h6" fontWeight={700}>
                                {t("EMPLOYEE_INFO")}
                            </Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography color="text.secondary">
                                    {t("NAME")}:
                                </Typography>
                                <Typography fontWeight={600}>
                                    {invoice.employee.name}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography color="text.secondary">
                                    {t("USERNAME")}:
                                </Typography>
                                <Typography fontWeight={600}>
                                    {invoice.employee.username}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Customer/Party Info */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <BusinessIcon color="secondary" />
                            <Typography variant="h6" fontWeight={700}>
                                {t("CUSTOMER_INFO")}
                            </Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography color="text.secondary">
                                    {t("NAME")}:
                                </Typography>
                                <Typography fontWeight={600}>
                                    {invoice.otherParty.name}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography color="text.secondary">
                                    {t("PHONE")}:
                                </Typography>
                                <Typography fontWeight={600}>
                                    {invoice.otherParty.phone || "N/A"}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Total Price Banner */}
            <Paper
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: "grey.50",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 2,
                    border: "1px solid #e0e0e0",
                }}
            >
                <Typography variant="h6" color="text.secondary">
                    {t("TOTAL_PRICE")}:
                </Typography>
                <Typography variant="h4" color="primary" fontWeight={700}>
                    ${invoice.totalPrice}
                </Typography>
            </Paper>

            {/* Items Table */}
            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Box
                    sx={{
                        p: 2,
                        bgcolor: "grey.50",
                        borderBottom: "1px solid #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <InventoryIcon color="action" />
                    <Typography variant="h6" fontWeight={700}>
                        {t("ITEMS")} ({items.length})
                    </Typography>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "white" }}>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("ITEM_NAME") || "Item Name"}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("PRICE")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("QUANTITY")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("REMAINING_STOCK")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    {t("EXPIRY_DATE")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell fontWeight={600}>
                                        {item.name}
                                    </TableCell>
                                    <TableCell>${item.price}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.quantity}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {item.quantityRemaining}
                                    </TableCell>
                                    <TableCell>
                                        {item.expiryDate
                                            ? formatDate(item.expiryDate)
                                            : "N/A"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default BuyInvoice;
